import * as Msdyn365 from '@msdyn365-commerce/core';
import { observer } from 'mobx-react';
import * as React from 'react';

import { IGhMediaGalleryData } from './gh-media-gallery.data';
import { IGhMediaGalleryProps } from './gh-media-gallery.props.autogenerated';

export interface IGhMediaGalleryImage {
    key: string;
    src: string;
    thumbSrc: string;
    zoomSrc: string;
    altText: string;
}

export interface IGhMediaGalleryViewProps extends IGhMediaGalleryProps<IGhMediaGalleryData> {
    className: string;
    enableZoom: boolean;
    images: IGhMediaGalleryImage[];
}

// Fallbacks so an unconfigured module never requests full-size originals.
const DEFAULT_STAGE_QUERY = 'w=767&h=907&q=80&m=1&f=jpg';
const DEFAULT_THUMB_QUERY = 'w=176&h=176&q=80&m=1&f=jpg';
const DEFAULT_ZOOM_QUERY = 'w=1200&h=1418&q=80&m=1&f=jpg';

const appendQuery = (uri: string, query: string): string => (uri.includes('?') ? `${uri}&${query}` : `${uri}?${query}`);

// Site Builder keeps mode/background outside q; the SDK Image merges them, so we must too.
const applyImageOptions = (query: string, settings: Msdyn365.IImageSettings | undefined): string => {
    if (!settings) {
        return query;
    }
    const params = new URLSearchParams(query);
    if (settings.mode) {
        params.set('m', settings.mode.toString());
    }
    if (settings.backgroundColor) {
        params.set('b', settings.backgroundColor);
        params.set('f', 'jpg');
    }
    if (settings.cropFocalRegion) {
        params.set('cropfocalregion', 'true');
    }
    return params.toString();
};

/**
 * One resized variant per role keeps CDN cache density high - the same URL is
 * requested by every viewport instead of one per breakpoint.
 */
const pickQuery = (settings: Msdyn365.IImageSettings | undefined, fallback: string): string => {
    const viewports = settings?.viewports;
    const setting = viewports?.lg ?? viewports?.xl ?? viewports?.md ?? viewports?.sm ?? viewports?.xs;
    return applyImageOptions(setting?.q ?? fallback, settings);
};

@observer
class GhMediaGallery extends React.PureComponent<IGhMediaGalleryProps<IGhMediaGalleryData>> {
    public render(): JSX.Element | null {
        const { config, data } = this.props;
        const stageQuery = pickQuery(config.galleryImageSettings, DEFAULT_STAGE_QUERY);
        const thumbQuery = pickQuery(config.thumbnailImageSettings, DEFAULT_THUMB_QUERY);
        const zoomQuery = pickQuery(config.zoomedImageSettings, DEFAULT_ZOOM_QUERY);
        const productName = data?.product?.result?.Name ?? '';

        const curated = (config.images ?? [])
            .map(image => ({ uri: image.src ?? '', altText: image.altText ?? '' }))
            .filter(source => source.uri.length > 0);
        const productMedia = (data?.mediaLocationsForSelectedVariant?.result ?? [])
            .map(location => ({ uri: location.Uri ?? '', altText: location.AltText ?? '' }))
            .filter(source => source.uri.length > 0);

        const images: IGhMediaGalleryImage[] = (curated.length > 0 ? curated : productMedia).map((source, index) => ({
            key: `${index}-${source.uri}`,
            src: appendQuery(source.uri, stageQuery),
            thumbSrc: appendQuery(source.uri, thumbQuery),
            zoomSrc: appendQuery(source.uri, zoomQuery),
            altText: source.altText || productName
        }));

        const viewProps: IGhMediaGalleryViewProps = {
            ...this.props,
            className: config.className ?? '',
            enableZoom: config.enableZoom !== false,
            images
        };

        return this.props.renderView(viewProps) as React.ReactElement;
    }
}

export default GhMediaGallery;
